import React, { useEffect, useState } from 'react'
import DataTable from './DataTable'
import { ConfirmModal } from './ConfirmModal';
import { EditModal } from './EditModal';
import icon_ai from '../assets/actions/icon_ai.png';
import { generateImage } from '../api/ai';
import { Article, Story } from '../types';
import ImagePicker from './ImagePicker';
import Modal from './Modal';
import { createArticle, deleteArticle, updateArticle } from '../api/articleApi';
import { uploadImage } from '../api/imageApi';
import Toast from './Toast';
import './Articles.css';

// Helper function to convert data URL to File object
const dataUrlToFile = (dataUrl: string, filename: string): File => {
  const arr = dataUrl.split(',');
  const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/jpeg';
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new File([u8arr], filename, { type: mime });
};

export const Articles = () => {
  const [filters, setFilters] = useState({});
  const [reload, setReload] = useState(false);
  const [showModalConfirmDelete, setShowModalConfirmDelete] = useState(false);
  const [showArticleModal, setShowArticleModal] = useState(false);
  const [showModalEdit, setShowModalEdit] = useState(false);
  const [isImagePickerOpen, setIsImagePickerOpen] = useState(false);
  const [showAIImageConfirmModal, setShowAIImageConfirmModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [creationType, setCreationType] = useState('edit');
  const [toastType, setToastType] = useState<'success' | 'error'>('success');
  const [showAIModal, setShowAIModal] = useState(false);
  const [isGeneratingImage, setIsGeneratingImage] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [article, setArticle] = useState<Article | Story | null>(null);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    image_url: ''
  });

  const columnDefinitions = [
    { id: 'title', label: 'Título', align: 'left' },
    { id: 'name', label: 'Creado por', align: 'center' },
    { id: 'created_at', label: 'Fecha', align: 'center' },
    {
      id: 'planned', label: 'Planificado', align: 'center',
      accessorFn: (row: any) => {
        return row.planned && Array.isArray(row.planned)
          ? row.planned.map((item: string, index: number) => (
            <React.Fragment key={index}>
              {item !== 'null' ? item : '-'}
              <br />
            </React.Fragment>
          ))
          : 'No';
      },
    },
    {
      id: 'published', label: 'Publicado', align: 'center',
      accessorFn: (row: any) => {
        return row.published && Array.isArray(row.planned)
          ? row.published.map((item: string, index: number) => (
            <React.Fragment key={index}>
              {item !== 'null' ? item : '-'}
              <br />
            </React.Fragment>
          ))
          : 'No';
      },
    },
    {
      id: 'actions',
      type: 'actions',
      label: '',
      align: 'center',
      actions: [
        {
          type: 'edit',
          onClick: (row: any) => {
            setArticle(row);
            setCreationType('edit');
            setShowModalEdit(true);
          },
        },
        {
          type: 'delete',
          onClick: (row: any) => {
            setArticle(row);
            setShowModalConfirmDelete(true);
          },
        },
      ],
    },
  ];

  useEffect(() => {
    setSelectedImage(article?.image_url || null);
  }, [article]);

  useEffect(() => {
    if (article) {
      setFormData({
        title: article.title || '',
        content: 'content' in article ? article.content : '',
        image_url: article.image_url || ''
      });
    }
  }, [article]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageSelect = (url: string) => {
    if (!url) return;
    
    setSelectedImage(url);
    setFormData(prev => ({
      ...prev,
      image_url: url
    }));
  };

  const generateAIImage = () => {
    if (isSaving) {
      return;
    }
    if (!formData.title) {
      setToastMessage('No se puede generar una imagen sin título');
      setToastType('error');
      setShowToast(true);
      return;
    }
    // add modal to confirm the generation
    setShowAIImageConfirmModal(true);
  };

  const handleConfirmAIImageGeneration = () => {
    setShowAIImageConfirmModal(false);
    setIsGeneratingImage(true);
    console.log('generateAIImage');
    generateImage(formData.title)
      .then(async (response) => {
        console.log('🎯 AI Image generation response (Articles):', response);
        if (response && 'fileUrl' in response) {
          // Articles don't use text overlay - just use the original image
          setSelectedImage(response.fileUrl);
          setFormData(prev => ({
            ...prev,
            image_url: response.fileUrl
          }));
        } else {
          console.log('❌ No valid response from image generation');
        }
      })
      .catch((error) => {
        console.error('Error generating image:', error);
        setToastMessage('Error al generar la imagen. Por favor, inténtelo de nuevo.');
        setToastType('error');
        setShowToast(true);
      })
      .finally(() => {
        setIsGeneratingImage(false);
      });
  };

  const handleConfirmDelete = async () => {
    if (!article?.id) return;

    setIsDeleting(true);
    try {
      const response = await deleteArticle(article.id);
      if (response?.status === 'success') {
        setToastMessage('Artículo eliminado correctamente');
        setToastType('success');
        setShowToast(true);
        setReload((prev) => !prev);
      } else {
        setToastMessage('Error al eliminar el artículo');
        setToastType('error');
        setShowToast(true);
      }
    } catch (error) {
      console.error('Error deleting article:', error);
      setToastMessage('Error al eliminar el artículo');
      setToastType('error');
      setShowToast(true);
    } finally {
      setIsDeleting(false);
      setShowModalConfirmDelete(false);
      setArticle(null);
    }
  };

  const handleConfirmEdit = async () => {
    setIsSaving(true);

    if (creationType === 'create') {
      try {
        const createdArticle = await createArticle(formData);
        if (createdArticle) {
          setReload((prev) => !prev);
          setToastMessage('Artículo creado correctamente');
          setToastType('success');
          setShowToast(true);
          setShowModalEdit(false);
          setArticle(null);
        } else {
          setToastMessage('Error al crear el artículo');
          setToastType('error');
          setShowToast(true);
        }
      } catch (error) {
        console.error('Error creating article:', error);
        setToastMessage('Error al crear el artículo');
        setToastType('error');
        setShowToast(true);
      } finally {
        setIsSaving(false);
      }
      return;
    }

    // Update article
    if (!article?.id) {
      setIsSaving(false);
      return;
    }

    const updatedArticle = {
      ...article,
      ...formData
    };

    try {
      const result = await updateArticle(updatedArticle);
      if (result) {
        setReload((prev) => !prev);
        setToastMessage('Artículo actualizado correctamente');
        setToastType('success');
        setShowToast(true);
        setShowModalEdit(false);
        setArticle(null);
      } else {
        setToastMessage('Error al actualizar el artículo');
        setToastType('error');
        setShowToast(true);
      }
    } catch (error) {
      console.error('Error updating article:', error);
      setToastMessage('Error al actualizar el artículo');
      setToastType('error');
      setShowToast(true);
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    setIsImagePickerOpen(false);
  };

  function handleShowArticle(
    article: Article | Story,
    setShowArticleModal: React.Dispatch<React.SetStateAction<boolean>>,
    setArticle: React.Dispatch<React.SetStateAction<Article | Story | null>>): void {
    setArticle(article);
    setShowArticleModal(true);
  }

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
  };

  const handleAIGenerate = (generatedContent: { title: string; content: string }) => {
    setFormData({
      title: generatedContent.title,
      content: generatedContent.content,
      image_url: ''
    });
    setCreationType('create');
    setShowModalEdit(true);
  };

  const handlePartialGenerate = (partialContent: { title: string; content: string }) => {
    setFormData(prev => ({
      ...prev,
      title: partialContent.title,
      content: partialContent.content
    }));
  };

  return (
    <div>
      <h2 className='page-title'>Artículos</h2>
      <DataTable
        columnDefinitions={columnDefinitions}
        filters={filters}
        refresh={reload}
        onShowArticle={(article) => handleShowArticle(article, setShowArticleModal, setArticle)}
        dataSource='articles'
      />

      <div className='create-article-button-container'>
        <button
          className='button-primary'
          onClick={() => {
            setArticle(null);
            setFormData({ title: '', content: '', image_url: '' });
            setCreationType('create');
            setShowModalEdit(true);
          }}
        >
          Crear artículo
        </button>
      </div>

      {showToast && (
        <Toast
          message={toastMessage}
          type={toastType}
          onClose={() => setShowToast(false)}
        />
      )}

      <ConfirmModal
        show={showModalConfirmDelete}
        title="Borrar artículo"
        text={`¿Confirma la eliminación de "${article?.title || ''}"?`}
        onClose={() => {
          if (!isDeleting) {
            setShowModalConfirmDelete(false);
            setArticle(null);
          }
        }}
        onConfirm={handleConfirmDelete}
        isLoading={isDeleting}
      />

      <ConfirmModal
        show={showAIImageConfirmModal}
        title="Generar imagen con IA"
        text="Esta acción costará alrededor de 0.05 USD"
        onClose={() => setShowAIImageConfirmModal(false)}
        onConfirm={handleConfirmAIImageGeneration}
        confirmButtonColor="blue"
      />

      <EditModal
        type='article'
        show={showModalEdit}
        title={creationType === 'create' ? 'Agregar artículo' : 'Editar artículo'}
        onClose={() => {
          if (!isSaving) {
            setShowModalEdit(false);
            setArticle(null);
          }
        }}
        onConfirm={handleConfirmEdit}
        onGenerate={handleAIGenerate}
        onPartialGenerate={handlePartialGenerate}
        isGenerating={isGeneratingImage || isSaving}
      >
        <form onSubmit={handleSubmit}>
          <div>
            <label>Título</label><br />
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
            />
          </div>
          <div>
            <label>Artículo</label><br />
            <textarea
              name="content"
              value={formData.content}
              onChange={handleInputChange}
            />
          </div>
          <div>
            <label>Imagen</label>
            <img
              src={icon_ai}
              height="30"
              width="30"
              alt="icon_ai"
              style={{ 
                marginLeft: '10px', 
                cursor: isSaving ? 'not-allowed' : 'pointer',
                opacity: isSaving ? 0.5 : 1
              }}
              onClick={generateAIImage}
            />
            <br />
            <div>
              {isGeneratingImage ? (
                <div className="image-loading">
                  <div className="dots">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                  <div className="loading-text">Generando imagen...</div>
                </div>
              ) : selectedImage ? (
                <img
                  className='edit-form-image'
                  src={selectedImage}
                  alt="Selected"
                />
              ) : (
                <div 
                  className="image-placeholder clickable-placeholder"
                  onClick={(e) => {
                    e.preventDefault();
                    if (!isSaving) {
                      setIsImagePickerOpen(true);
                    }
                  }}
                >
                  Ninguna imagen seleccionada
                </div>
              )}
              <br />
              <button
                type="button"
                className='button-primary'
                onClick={(e) => {
                  e.preventDefault();
                  if (!isSaving && !isGeneratingImage) {
                    setIsImagePickerOpen(true);
                  }
                }}
                disabled={isGeneratingImage || isSaving}
              >
                {isGeneratingImage ? "Generando..." : isSaving ? "Guardando..." : "Cambiar imagen"}
              </button>
              <ImagePicker
                isOpen={isImagePickerOpen}
                onClose={handleClose}
                onSelect={handleImageSelect}
              />
            </div>
          </div>
        </form>
      </EditModal>

      <Modal
        isOpen={showArticleModal}
        onClose={() => {
          setShowArticleModal(false);
          setArticle(null);
        }}
        title='Ver artículo'
      >
        <div>
          <h2 style={{ paddingTop: '10px' }}>{article?.title || "No Title"}</h2>
          <div className="modal-image-container">
            <img src={article?.image_url} alt="Article Image" className="modal-image" />
          </div>
          <span>{article && 'content' in article ? article.content : "No Content Available"}</span>
        </div>
      </Modal>
    </div>
  )
}
